import dayjs from "dayjs";
import * as htmlToImage from "html-to-image";
import { ChevronDown, Copy, Download, Globe, Link } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { getJobDeadline, isOpenJob, type JobStatusFields } from "@/app/handlers/jobStatus";

import Spinner from "../../components/Spinner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const [HEIGHT, WIDTH] = [1350, 1080]; // Default dimensions for the image

export interface PosterJob extends JobStatusFields {
	_id?: string;
	jobTitle?: string;
	jobExpertise?: string;
	jobLocation?: {
		city?: string;
		region?: string;
	};
}

interface TemplateProps {
	companyName?: string;
	jobs: PosterJob[];
	companyLink?: string;
	logoUrl?: string;
}

type GenerateMultiJobImageProps = Omit<TemplateProps, "jobs"> & {
	jobs?: PosterJob[];
};

const TEMPLATES = [
	{ id: "template1", component: TemplateOne, name: "Template One" },
	{ id: "template2", component: TemplateTwo, name: "Template Two" },
];

/** A poster advertises roles people can still apply to, so closed ones never reach a template. */
function formatDeadline(job: PosterJob): string {
	const deadline = getJobDeadline(job);
	return deadline ? dayjs(deadline).format("MMM DD, YYYY") : "";
}

function GenerateMultiJobImage({ companyName, jobs, companyLink, logoUrl }: GenerateMultiJobImageProps) {
	const imageRef = React.useRef<HTMLDivElement>(null);
	const jobsDropdownRef = React.useRef<HTMLDivElement>(null);
	const [isDownloading, setIsDownloading] = React.useState(false);
	const [isCopying, setIsCopying] = React.useState(false);
	const [jobsDropdownOpen, setJobsDropdownOpen] = React.useState(false);

	// Closed roles are dropped before anything else so the counts, the category list, the
	// checkbox list and the poster all describe the same set of still-open positions.
	const allJobs = React.useMemo(() => (Array.isArray(jobs) ? jobs : []), [jobs]);
	const openJobs = React.useMemo(() => allJobs.filter(isOpenJob), [allJobs]);
	const hiddenClosedCount = allJobs.length - openJobs.length;

	const [selectedTemplate, setSelectedTemplate] = React.useState(TEMPLATES[0].id);
	const [selectedCategory, setSelectedCategory] = React.useState("all");
	const [selectedJobIds, setSelectedJobIds] = React.useState<number[]>(() => openJobs.map((_, i) => i));

	// Close dropdown when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (jobsDropdownRef.current && !jobsDropdownRef.current.contains(event.target as Node)) {
				setJobsDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Extract unique categories from the open jobs
	const categories = React.useMemo(() => {
		const uniqueCategories = [...new Set(openJobs.map((job) => job.jobExpertise).filter(Boolean))];
		return uniqueCategories.sort();
	}, [openJobs]);

	// Filter jobs based on category and selection
	const filteredJobs = React.useMemo(() => {
		return openJobs.filter((job, index) => {
			const matchesCategory = selectedCategory === "all" || job.jobExpertise === selectedCategory;
			const isSelected = selectedJobIds.includes(index);
			return matchesCategory && isSelected;
		});
	}, [openJobs, selectedCategory, selectedJobIds]);

	const toggleJob = (index: number) => {
		setSelectedJobIds((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
	};

	const selectAllJobs = () => setSelectedJobIds(openJobs.map((_, i) => i));
	const deselectAllJobs = () => setSelectedJobIds([]);

	const downloadImage = async () => {
		if (imageRef.current && !isDownloading) {
			setIsDownloading(true);
			try {
				const dataUrl = await htmlToImage.toPng(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				const link = document.createElement("a");
				link.download = `${companyName}-job-openings.png`;
				link.href = dataUrl;
				link.click();
			} catch (error) {
				toast.error("Failed to download image" + error);
			} finally {
				setIsDownloading(false);
			}
		}
	};

	const copyToClipboard = async () => {
		if (imageRef.current && !isCopying) {
			setIsCopying(true);
			try {
				const blob = await htmlToImage.toBlob(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
				toast.success("Image copied to clipboard!");
			} catch (error) {
				console.error(error);
				toast.error("Failed to copy image to clipboard");
			} finally {
				setIsCopying(false);
			}
		}
	};

	const template = TEMPLATES.find((t) => t.id === selectedTemplate)?.component || TEMPLATES[0].component;

	return (
		<div className="p-6 flex flex-col items-center justify-center">
			<div className="flex gap-2 mb-4 justify-start items-center w-full flex-wrap">
				{TEMPLATES.map((template) => (
					<Button
						key={template.id}
						onClick={() => setSelectedTemplate(template.id)}
						variant={selectedTemplate === template.id ? "default" : "outline"}
						size="sm"
					>
						{template.name}
					</Button>
				))}

				<div className="h-6 w-px bg-gray-300 mx-1" />

				{/* Category Filter */}
				<Select value={selectedCategory} onValueChange={setSelectedCategory}>
					<SelectTrigger className="w-40 h-8 text-xs">
						<SelectValue placeholder="Category" />
					</SelectTrigger>
					<SelectContent className="z-200 bg-white">
						<SelectItem value="all">All Categories</SelectItem>
						{categories.map((category) => (
							<SelectItem key={category} value={category}>
								{category}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{/* Job Selection Dropdown */}
				<div className="relative" ref={jobsDropdownRef}>
					<Button
						variant="outline"
						size="sm"
						className="h-8 text-xs"
						onClick={() => setJobsDropdownOpen(!jobsDropdownOpen)}
					>
						Jobs ({selectedJobIds.length}/{openJobs.length})
						<ChevronDown className={`ml-1 h-3 w-3 transition-transform ${jobsDropdownOpen ? "rotate-180" : ""}`} />
					</Button>

					{jobsDropdownOpen && (
						<div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg z-50">
							<div className="flex gap-1 p-1 border-b sticky top-0 bg-white">
								<Button variant="ghost" size="sm" className="h-6 text-xs flex-1" onClick={selectAllJobs}>
									Select All
								</Button>
								<Button variant="ghost" size="sm" className="h-6 text-xs flex-1" onClick={deselectAllJobs}>
									Deselect All
								</Button>
							</div>
							{openJobs.map((job, index) => (
								<label
									key={job._id || index}
									className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-xs"
								>
									<input
										type="checkbox"
										checked={selectedJobIds.includes(index)}
										onChange={() => toggleJob(index)}
										className="rounded border-gray-300"
									/>
									<span className="truncate">{job.jobTitle}</span>
								</label>
							))}
						</div>
					)}
				</div>

				<span className="text-xs text-gray-500 ml-2">
					{filteredJobs.length} jobs in image
					{hiddenClosedCount > 0 && ` • ${hiddenClosedCount} closed hidden`}
				</span>
			</div>

			<div
				className="shadow-lg bg-red-300 grid grid-cols-3"
				style={{
					width: WIDTH / 2,
					height: HEIGHT / 2,
				}}
				ref={imageRef}
			>
				{template({ companyName, jobs: filteredJobs, companyLink, logoUrl })}
			</div>

			{/* Action Buttons */}
			<div className="flex justify-center gap-4 mt-4">
				<button
					onClick={downloadImage}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-primary-800 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isDownloading ? <Spinner /> : <Download size={20} />}
					{isDownloading ? "Downloading..." : "Download Image"}
				</button>
				<button
					onClick={copyToClipboard}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isCopying ? <Spinner /> : <Copy size={20} />}
					{isCopying ? "Copying..." : "Copy to Clipboard"}
				</button>
			</div>
		</div>
	);
}

export default GenerateMultiJobImage;

function TemplateOne({ companyName, jobs, companyLink, logoUrl }: TemplateProps) {
	return (
		<>
			<div className="col-span-2 bg-primary-800 relative">
				<div className="m-10 mb-6 mr-2">
					<img src="/media-kit/logos/logo-dark.svg" className="w-24 -ml-2 mb-1" />
					<h3 className="uppercase text-white" contentEditable>
						{companyName} is
					</h3>
					<h2 className="text-5xl text-white font-plus-jakarta">HIRING</h2>
				</div>

				<div>
					<span className="bg-white text-primary-800 font-semibold py-2 pr-4 rounded-r-full pl-10">
						OPEN POSITIONS:
					</span>
				</div>
				<div className="ml-10 mt-2">
					<ul className="list-disc p-3 text-white">
						{jobs.map((job, index) => (
							<li key={job._id || index} className="text-sm mb-2">
								<p className="leading-tight font-medium font-plus-jakarta text-white" contentEditable>
									{job.jobTitle}
								</p>
								<p className="text-xs text-gray-100 leading-tight">
									<span className="italic" contentEditable>
										{job.jobLocation?.city}
									</span>{" "}
									• <span>{formatDeadline(job)}</span>
								</p>
							</li>
						))}
					</ul>
				</div>
				<div className="absolute pl-10 pb-6 bottom-4">
					<div className="uppercase text-primary-800 bg-white font-semibold w-fit mb-4 px-6 rounded-full py-2">
						Apply Now
					</div>
					<div className="text-xs flex pr-2 gap-3 items-center">
						<span className="text-white aspect-square rounded-full flex items-center justify-center">
							<Link className="inline-block" size={16} />
						</span>
						<span className="leading-none text-white text-sm" contentEditable>
							{companyLink}
						</span>
					</div>
				</div>
			</div>
			<div className="bg-white relative flex items-center justify-center">
				<img
					src={logoUrl || "https://via.placeholder.com/150"}
					alt={`${companyName} logo`}
					className="right-6 h-12 w-12 object-cover absolute top-10"
				/>
				<img src="/images/job-vacancy-background-flat-style.png" className="h-96 object-cover" />
				<div className="bottom-10 absolute">
					<div className="flex items-center justify-between pb-8">
						<div className="text-primary-800 text-right mr-2">
							<p className="text-[8px] leading-none">For More Information</p>
							<p className="font-semibold text-xs">www.dproz.com</p>
						</div>
						<Globe className="text-primary-800" size={36} />
					</div>
				</div>
			</div>
		</>
	);
}

function TemplateTwo({ companyName, jobs, companyLink, logoUrl }: TemplateProps) {
	return (
		<>
			<div className="col-span-2 bg-white relative">
				<div className="m-10 mb-6 mr-2">
					<img src="/images/logo.png" className="w-24 -ml-2 mb-1" />
					<h3 className="uppercase" contentEditable>
						{companyName} is
					</h3>
					<h2 className="text-5xl text-primary-800 font-plus-jakarta">HIRING</h2>
				</div>

				<div>
					<span className="bg-primary-800 text-white py-2 pr-4 rounded-r-full pl-10">OPEN POSITIONS:</span>
				</div>
				<div className="ml-10 mt-2">
					<ul className="list-disc p-3">
						{jobs.map((job, index) => (
							<li key={job._id || index} className="text-sm mb-2">
								<p className="leading-tight" contentEditable>
									{job.jobTitle}
								</p>
								<p className="text-xs text-gray-600 leading-tight">
									<span className="italic" contentEditable>
										{job.jobLocation?.city}
									</span>{" "}
									• <span>{formatDeadline(job)}</span>
								</p>
							</li>
						))}
					</ul>
				</div>
				<div className="absolute pl-10 pb-6 bottom-4">
					<div className="uppercase bg-primary-800 text-white w-fit mb-4 px-6 rounded-full py-2">Apply Now</div>
					<div className="text-xs flex pr-2 gap-2 items-center">
						<span className="bg-primary-800 text-white h-6 w-6 aspect-square rounded-full flex items-center justify-center">
							<Link className="inline-block" size={12} />
						</span>
						<span className="font-medium leading-none text-sm" contentEditable>
							{companyLink}
						</span>
					</div>
				</div>
			</div>
			<div className="bg-primary-800 relative flex items-center justify-center">
				<img
					src={logoUrl || "https://via.placeholder.com/150"}
					alt={`${companyName} logo`}
					className="right-6 h-12 w-12 object-cover absolute top-10"
				/>
				<img src="/images/job-vacancy-background-flat-style.png" className="h-96 object-cover" />
				<div className="bottom-10 absolute">
					<div className="flex items-center justify-between bg-primary-800 pb-8">
						<div className="text-white text-right mr-2">
							<p className="text-[8px] leading-none">For More Information</p>
							<p className="font-medium text-sm">www.dproz.com</p>
						</div>
						<Globe className="text-white" size={36} />
					</div>
				</div>
			</div>
		</>
	);
}
